<script setup>
  import { ref, onMounted, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import Pagination from '../Pagination.vue';

  const { t } = useI18n();
  const i18nScope = ref('components.library_catalog');

  const props = defineProps({
    visibleColumns: {
      type: Object,
      default: () => ({
        title: true, isbn: true, author: true, publisher: true,
        category: false, format: true, language: true, audience: false,
        acquisitionDate: true, expirationDate: true, licenses: true,
        totalLoans: true, reservations: true
      })
    },
    searchQuery: {
      type: String,
      default: ''
    },
    selectedLanguage: {
      type: String,
      default: null
    },
    selectedFormat: {
      type: String,
      default: null
    },
    selectedCategory: {
      type: String,
      default: null
    },
    selectedAudience: {
      type: String,
      default: null
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    acquisitionStartDate: {
      type: String,
      default: null
    },
    acquisitionEndDate: {
      type: String,
      default: null
    },
    currentLibrary: {
      type: Object,
      default: null
    }
  });

  const licensesLoading = ref(false);
  const licenses = ref([]);
  const currentPage = ref(1);
  const itemsPerPage = ref(20);
  const maxPagesToShow = ref(12);
  const totalItems = ref(0);
  const sortBy = ref('title');
  const sortOrder = ref('asc');

  const fetchLicenses = async () => {
    licensesLoading.value = true;

    try {
      let url = `/api/library_catalog/list.json?page=${currentPage.value}&per_page=${itemsPerPage.value}&library_id=${props.currentLibrary.id}`;
      
      // Custom filters
      if (props.startDate) url += `&start_date=${props.startDate}`;
      if (props.endDate) url += `&end_date=${props.endDate}`;
      if (props.acquisitionStartDate) url += `&acquisition_start_date=${props.acquisitionStartDate}`;
      if (props.acquisitionEndDate) url += `&acquisition_end_date=${props.acquisitionEndDate}`;
      if (props.selectedLanguage) url += `&language=${props.selectedLanguage}`;
      if (props.selectedFormat) url += `&book_format=${props.selectedFormat}`;
      if (props.selectedCategory) url += `&category=${props.selectedCategory}`;
      if (props.selectedAudience) url += `&audience=${props.selectedAudience}`;
      url += `&sort_by=${sortBy.value}&sort_order=${sortOrder.value}`;

      const response = await axios.get(url);
      
      licenses.value = response.data.licenses;
      totalItems.value = response.data.total;

    } catch (error) {
      console.error('Error fetching licenses:', error);
      licenses.value = [];
      totalItems.value = 0;
    } finally {
      licensesLoading.value = false;
    }
  };

  const expandedRows = ref({});
  const rowCopies = ref({});
  const rowCopiesLoading = ref({});

  const toggleRow = async (title) => {
    if (expandedRows.value[title.offer_id]) {
      delete expandedRows.value[title.offer_id];
      return;
    }
    expandedRows.value[title.offer_id] = true;
    if (!rowCopies.value[title.offer_id]) {
      rowCopiesLoading.value[title.offer_id] = true;
      try {
        const res = await axios.get(`/api/library_catalog/offer_copies.json?offer_id=${title.offer_id}&library_id=${props.currentLibrary.id}`);
        rowCopies.value[title.offer_id] = res.data;
      } catch (error) {
        console.error('Error fetching offer copies:', error);
        rowCopies.value[title.offer_id] = [];
      } finally {
        delete rowCopiesLoading.value[title.offer_id];
      }
    }
  };

  const clearLicenses = () => {
    licenses.value = [];
    currentPage.value = 1;
  };

  const handleSort = (column) => {
    if (sortBy.value === column) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      sortBy.value = column;
      sortOrder.value = 'asc';
    }
    fetchLicenses();
  };

  const getSortIcon = (column) => {
    if (sortBy.value !== column) return '';
    return sortOrder.value === 'asc' ? '↑' : '↓';
  };

  const handlePageChange = (page) => {
    currentPage.value = page;
    fetchLicenses();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const year = String(date.getFullYear()).slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const visibleColumnsCount = computed(() =>
    Object.values(props.visibleColumns).filter(Boolean).length + 1
  );

  defineExpose({
    fetchLicenses,
    clearLicenses
  });

  onMounted(() => {
    fetchLicenses();
  });
</script>

<template>
  <div class="library-catalog-table">
    <!-- Loading Spinner -->
    <div v-if="licensesLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ t(`${i18nScope}.loading`) }}</span>
      </div>
    </div>

    <!-- Table -->
    <div v-else-if="licenses.length > 0" class="table-responsive">
      <table class="table table-striped">
        <thead class="table-light">
          <tr>
            <th v-if="visibleColumns.title" scope="col" class="sortable" @click="handleSort('title')">
              {{ t(`${i18nScope}.table.title`) }} {{ getSortIcon('title') }}
            </th>
            <th v-if="visibleColumns.isbn" scope="col">{{ t(`${i18nScope}.table.isbn`) }}</th>
            <th v-if="visibleColumns.author" scope="col" class="sortable" @click="handleSort('author')">
              {{ t(`${i18nScope}.table.author`) }} {{ getSortIcon('author') }}
            </th>
            <th v-if="visibleColumns.publisher" scope="col" class="sortable" @click="handleSort('publisher')">
              {{ t(`${i18nScope}.table.publisher`) }} {{ getSortIcon('publisher') }}
            </th>
            <th v-if="visibleColumns.category" scope="col" class="sortable" @click="handleSort('category')">
              {{ t(`${i18nScope}.table.category`) }} {{ getSortIcon('category') }}
            </th>
            <th v-if="visibleColumns.format" scope="col" class="sortable" @click="handleSort('format')">
              {{ t(`${i18nScope}.table.format`) }} {{ getSortIcon('format') }}
            </th>
            <th v-if="visibleColumns.language" scope="col" class="sortable" @click="handleSort('language')">
              {{ t(`${i18nScope}.table.language`) }} {{ getSortIcon('language') }}
            </th>
            <th v-if="visibleColumns.audience" scope="col">{{ t(`${i18nScope}.table.audience`) }}</th>
            <th v-if="visibleColumns.acquisitionDate" scope="col" class="sortable" @click="handleSort('acquisitionDate')">
              {{ t(`${i18nScope}.table.acquisition_date`) }} {{ getSortIcon('acquisitionDate') }}
            </th>
            <th v-if="visibleColumns.expirationDate" scope="col" class="sortable" @click="handleSort('expirationDate')">
              {{ t(`${i18nScope}.table.expiration_date`) }} {{ getSortIcon('expirationDate') }}
            </th>
            <th v-if="visibleColumns.licenses" scope="col" class="text-center sortable" @click="handleSort('numberOfLicenses')">
              {{ t(`${i18nScope}.table.licenses`) }} {{ getSortIcon('numberOfLicenses') }}
            </th>
            <th v-if="visibleColumns.totalLoans" scope="col" class="text-center sortable" @click="handleSort('totalLoans')">
              {{ t(`${i18nScope}.table.total_loans`) }} {{ getSortIcon('totalLoans') }}
            </th>
            <th v-if="visibleColumns.reservations" scope="col" class="text-center sortable" @click="handleSort('currentReservations')">
              {{ t(`${i18nScope}.table.reservations`) }} {{ getSortIcon('currentReservations') }}
            </th>
            <th scope="col" class="details-col"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="title in licenses" :key="title.offer_id">
            <tr class="catalog-row" :class="{ 'expanded': expandedRows[title.offer_id] }" @click="toggleRow(title)">
              <td v-if="visibleColumns.title">
                <a :href="`/item/${title.item_id}`" class="title-link" target="_blank" @click.stop>
                  <svg class="external-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4.5 1.5H1.5v9h9V7.5M7.5 1.5H10.5V4.5M10.5 1.5L5.5 6.5"/></svg>{{ title.title }}
                </a>
                <svg class="title-chevron" :class="{ 'rotated': expandedRows[title.offer_id] }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>
              </td>
              <td v-if="visibleColumns.isbn">{{ title.isbn }}</td>
              <td v-if="visibleColumns.author">{{ title.author }}</td>
              <td v-if="visibleColumns.publisher">{{ title.publisher }}</td>
              <td v-if="visibleColumns.category">{{ title.category }}</td>
              <td v-if="visibleColumns.format">
                <span class="offer__format" :class="`offer__format--${title.format?.toLowerCase()}`">
                  {{ title.format }}
                </span>
              </td>
              <td v-if="visibleColumns.language" class="text-uppercase">{{ title.language }}</td>
              <td v-if="visibleColumns.audience">{{ title.audience }}</td>
              <td v-if="visibleColumns.acquisitionDate">{{ formatDateShort(title.acquisitionDate) }}</td>
              <td v-if="visibleColumns.expirationDate">{{ formatDateShort(title.expirationDate) }}</td>
              <td v-if="visibleColumns.licenses" class="text-center">{{ title.numberOfLicenses }}</td>
              <td v-if="visibleColumns.totalLoans" class="text-center">{{ title.totalLoans }}</td>
              <td v-if="visibleColumns.reservations" class="text-center">
                <span v-if="title.currentReservations > 0" class="badge reservation-badge">
                  {{ title.currentReservations }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="text-center details-cell">
                <svg class="chevron-icon" :class="{ 'rotated': expandedRows[title.offer_id] }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>
              </td>
            </tr>
            <tr v-if="expandedRows[title.offer_id]" class="expanded-row">
              <td :colspan="visibleColumnsCount">
                <div v-if="rowCopiesLoading[title.offer_id]" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>
                <table v-else class="table table-sm copies-table">
                  <thead>
                    <tr>
                      <th class="text-center">{{ t(`${i18nScope}.modal.columns.simultaneity`) }}</th>
                      <th>{{ t(`${i18nScope}.modal.columns.loan_count`) }}</th>
                      <th>{{ t(`${i18nScope}.modal.columns.duration`) }}</th>
                      <th>{{ t(`${i18nScope}.modal.columns.price`) }}</th>
                      <th>{{ t(`${i18nScope}.modal.columns.acquisition_date`) }}</th>
                      <th>{{ t(`${i18nScope}.modal.columns.expiration_date`) }}</th>
                      <th class="text-center">{{ t(`${i18nScope}.modal.columns.tokens_remaining`) }}</th>
                      <th class="text-center">{{ t(`${i18nScope}.modal.columns.status`) }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(copy, index) in rowCopies[title.offer_id]" :key="index">
                      <td class="text-center">
                        {{ copy.simultaneity ? t(`${i18nScope}.modal.columns.simultaneity_yes`) : t(`${i18nScope}.modal.columns.simultaneity_no`) }}
                      </td>
                      <td>{{ copy.loan_count }}</td>
                      <td>{{ copy.duration }}</td>
                      <td>{{ copy.price }}</td>
                      <td>{{ formatDate(copy.acquisition_date) }}</td>
                      <td>{{ formatDate(copy.expiration_date) }}</td>
                      <td class="text-center">{{ copy.tokens_remaining ?? '-' }}</td>
                      <td class="text-center">
                        <span class="badge copy-status" :class="copy.status === 'available' ? 'copy-status--active' : 'copy-status--inactive'">
                          {{ copy.status === 'available' ? t(`${i18nScope}.modal.columns.status_active`) : t(`${i18nScope}.modal.columns.status_inactive`) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="mt-4">
        <Pagination
          :currentPage="currentPage"
          :totalItems="totalItems"
          :itemsPerPage="itemsPerPage"
          :maxPagesToShow="maxPagesToShow"
          @page-change="handlePageChange"
        />
        <div class="text-center text-muted">
          {{ t(`${i18nScope}.table.showing`) }} {{ (currentPage - 1) * itemsPerPage + 1 }} - 
          {{ Math.min(currentPage * itemsPerPage, totalItems) }} {{ t(`${i18nScope}.table.of`) }} {{ totalItems }}
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div v-else class="text-center py-5">
      <p class="text-muted">{{ t(`${i18nScope}.no_results`) }}</p>
    </div>

    <div class="d-flex justify-content-center align-items-center mt-3">
      <BSpinner style="width: 3rem; height: 3rem;" variant="secondary" v-if="licensesLoading" />
    </div>
  </div>

</template>

<style scoped lang="scss">
  .library-catalog-table {
    min-height: 400px;
  }

  .table {
    font-size: 0.9rem;
  }

  .sortable {
    cursor: pointer;
    user-select: none;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .table-responsive {
    overflow-x: clip;
  }

  thead th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 10;
    font-weight: 600;
  }

  .badge {
    font-size: 0.85rem;
    padding: 0.35em 0.65em;
  }

  .spinner-border {
    width: 3rem;
    height: 3rem;
  }

  .catalog-row {
    cursor: pointer;

    .title-link {
      color: var(--primary);
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }

      .external-icon {
        width: 10px;
        height: 10px;
        margin-right: 4px;
        vertical-align: middle;
        opacity: 0.5;
        flex-shrink: 0;
      }
    }

    .details-cell .chevron-icon {
      width: 16px;
      height: 16px;
      color: #adb5bd;
      opacity: 0;
      transition: transform 0.2s, opacity 0.15s;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &:hover .details-cell .chevron-icon,
    &.expanded .details-cell .chevron-icon {
      opacity: 1;
    }
  }

  .expanded-row {
    background-color: #f8f9fa;

    > td {
      padding: 0;
      border-top: none;

      .copies-table {
        margin-bottom: 0;
      }
    }

    &:hover > td {
      background-color: #f8f9fa;
    }
  }

  .title-chevron {
    display: block;
    width: 16px;
    height: 16px;
    color: #adb5bd;
    margin-top: 3px;
    transition: transform 0.2s;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  .isbn-sub {
    color: #6c757d;
    margin-top: 1px;
  }

  .details-col {
    width: 28px;
  }

  .reservation-badge {
    background-color: var(--primary);
    color: white !important;
  }

  .copies-table {
    font-size: 0.85rem;
  }

  .copy-status {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;

    &--active {
      background-color: #d1e7dd;
      color: #0f5132;
    }

    &--inactive {
      background-color: #f8d7da;
      color: #842029;
    }
  }

</style>
